import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  TextField,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  Box,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";

import CloudDownloadOutlined from "@material-ui/icons/CloudDownloadOutlined";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";

import { ProductsByStoreService } from "../../services/ProductsByStoreService";
import MAlert from "../elements/MAlert";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
  },

  headerCard: {
    marginBottom: theme.spacing(3),
    background: "linear-gradient(135deg, #E91E63 0%, #E91E63 100%)",
    color: "white",
    borderRadius: 12,
  },

  headerTitle: {
    fontSize: "2rem",
    fontWeight: "bold",
  },

  filterSection: {
    backgroundColor: "#fff",
    padding: theme.spacing(3),
    borderRadius: 12,
    marginBottom: theme.spacing(3),
  },

  buttonWrapper: {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },

  buttonGroup: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
  },

  fetchButton: {
    background: "linear-gradient(45deg, #E91E63 30%, #E91E63 90%)",
    color: "#fff",

    "&:hover": {
      opacity: 0.9,
      background: "linear-gradient(45deg, #E91E63 30%, #E91E63 90%)",
    },
  },

  statsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },

  statCard: {
    background: "#fff",
    padding: theme.spacing(2),
    borderRadius: 10,
    minWidth: 200,
  },

  tableWrapper: {
    background: "#fff",
    borderRadius: 12,
    overflow: "hidden",
  },
}));

const STORE_OPTIONS = [
  { label: "F-10", value: "ften" },
  { label: "F-11", value: "feleven" },
  { label: "E-11/2", value: "eelevenby2" },
  { label: "E-11/3", value: "eelevenby3" },
  { label: "E-11/4", value: "eelevenby4" },
  { label: "Golra", value: "golra" },
  { label: "G-13", value: "gtharteen" },
  { label: "D-12", value: "dtwelve" },
];

const numberWithCommas = (x) => {
  if (x === undefined || x === null) return "0";

  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const ProductsByStore = () => {
  const classes = useStyles();

  const [selectedStore, setSelectedStore] = useState("");
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [filterProducts, setFilterProducts] = useState([]);
  const [csv, setCSV] = useState([]);

  const [alert, setAlert] = useState({
    show: false,
    message: "",
    type: "info",
  });

  const handleStoreChange = (e) => {
    setSelectedStore(e.target.value);
  };

  // FETCH PRODUCTS
  const fetchProducts = async () => {
    if (!selectedStore) {
      setAlert({
        show: true,
        message: "Please select a store",
        type: "warning",
      });

      return;
    }

    setLoading(true);

    try {
      const res =
        await ProductsByStoreService.getProductsByStore(selectedStore);

      const data = (res?.response || []).map((p) => {
        const noOfItems = p.quantity || 0;
        const price = p.price || 0;
        const discount = p.discount || 0;

        const totalPrice = price * noOfItems;
        const totalDiscount = discount * noOfItems;
        const netAmount = totalPrice - totalDiscount;

        return {
          ...p,
          noOfItems,
          totalPrice,
          totalDiscount,
          netAmount,
        };
      });

      setProducts(data);

      setCSV(
        data.map((p, i) => ({
          Sr: i + 1,
          ID: p.id,
          Name: p.title,
          Price: p.price,
          Quantity: p.noOfItems,
          TotalPrice: p.totalPrice,
          Discount: p.totalDiscount,
          NetAmount: p.netAmount,
        })),
      );

      setAlert({
        show: true,
        message: `${data.length} products fetched successfully`,
        type: "success",
      });
    } catch (err) {
      console.log(err);

      setProducts([]);
      setCSV([]);

      setAlert({
        show: true,
        message: "Failed to fetch products",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // FILTER PRODUCTS
  useEffect(() => {
    const filtered = products.filter((p) =>
      `${p.title || ""} ${p.id || ""}`
        .toLowerCase()
        .includes(filterText.toLowerCase()),
    );

    setFilterProducts(filtered);
  }, [products, filterText]);

  // TOTALS
  const totalItems = filterProducts.reduce(
    (sum, item) => sum + (item.noOfItems || 0),
    0,
  );

  const totalPriceAmount = filterProducts.reduce(
    (sum, item) => sum + (item.totalPrice || 0),
    0,
  );

  const totalDiscountAmount = filterProducts.reduce(
    (sum, item) => sum + (item.totalDiscount || 0),
    0,
  );

  const totalNetAmount = filterProducts.reduce(
    (sum, item) => sum + (item.netAmount || 0),
    0,
  );

  // TABLE COLUMNS
  const columns = useMemo(
    () => [
      {
        name: "Sr.#",
        width: "80px",
        cell: (row, index) => <span>{index + 1}</span>,
      },
      {
        name: "Name",
        selector: (row) => row.title,
        sortable: true,
        grow: 2,
      },
      {
        name: "Price",
        selector: (row) => numberWithCommas(row.price),
        sortable: true,
      },
      {
        name: "No of Items",
        selector: (row) => numberWithCommas(row.noOfItems),
        sortable: true,
      },
      {
        name: "Total Price",
        selector: (row) => numberWithCommas(row.totalPrice),
        sortable: true,
      },
      {
        name: "Discount",
        selector: (row) => numberWithCommas(row.totalDiscount),
        sortable: true,
      },
      {
        name: "Net Amount",
        selector: (row) => numberWithCommas(row.netAmount),
        sortable: true,
        grow: 1.5,
      },
    ],
    [],
  );

  // SEARCH COMPONENT
  const filterComponent = useMemo(
    () => (
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        label="Search Product"
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
      />
    ),
    [filterText],
  );

  return (
    <Box className={classes.root}>
      <MAlert alert={alert} setAlert={setAlert} />

      {/* HEADER */}
      <Paper className={classes.headerCard} elevation={3}>
        <Box p={3}>
          <Typography className={classes.headerTitle}>
            📦 Products By Store
          </Typography>
        </Box>
      </Paper>

      {/* FILTER SECTION */}
      <Paper className={classes.filterSection} elevation={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Store</InputLabel>

              <Select
                value={selectedStore}
                onChange={handleStoreChange}
                label="Store"
              >
                <MenuItem value="">Select</MenuItem>

                {STORE_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* BUTTONS */}
          <Grid item xs={12} md={8} className={classes.buttonWrapper}>
            <Box className={classes.buttonGroup}>
              <Button
                variant="contained"
                onClick={fetchProducts}
                disabled={loading}
                className={classes.fetchButton}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )
                }
              >
                Fetch
              </Button>

              <Button
                variant="contained"
                onClick={fetchProducts}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>

            {products.length > 0 && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <CSVLink
                  data={csv}
                  filename="products-by-store.csv"
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    variant="contained"
                    startIcon={<CloudDownloadOutlined />}
                  >
                    Download CSV
                  </Button>
                </CSVLink>
              </div>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* STATS */}
      {products.length > 0 && (
        <Box className={classes.statsContainer}>
          <Paper className={classes.statCard}>
            <Typography variant="subtitle2">Products</Typography>

            <Typography variant="h6">
              {numberWithCommas(filterProducts.length)}
            </Typography>
          </Paper>

          <Paper className={classes.statCard}>
            <Typography variant="subtitle2">Total Items</Typography>

            <Typography variant="h6">{numberWithCommas(totalItems)}</Typography>
          </Paper>

          <Paper className={classes.statCard}>
            <Typography variant="subtitle2">Total Price</Typography>

            <Typography variant="h6">
              Rs. {numberWithCommas(totalPriceAmount)}
            </Typography>
          </Paper>

          <Paper className={classes.statCard}>
            <Typography variant="subtitle2">Total Discount</Typography>

            <Typography variant="h6">
              Rs. {numberWithCommas(totalDiscountAmount)}
            </Typography>
          </Paper>

          <Paper className={classes.statCard}>
            <Typography variant="subtitle2">Net Amount</Typography>

            <Typography variant="h6">
              Rs. {numberWithCommas(totalNetAmount)}
            </Typography>
          </Paper>
        </Box>
      )}

      {/* TABLE */}
      {products.length > 0 && (
        <Paper className={classes.tableWrapper}>
          <DataTable
            columns={columns}
            data={filterProducts}
            pagination
            highlightOnHover
            striped
            responsive
            subHeader
            subHeaderComponent={filterComponent}
            persistTableHead
          />
        </Paper>
      )}
    </Box>
  );
};

export default ProductsByStore;
